import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { useLanguage } from '../contexts/LanguageContext';
import { 
    Watch, Smartphone, Heart, Moon, Footprints, Activity,
    CheckCircle2, XCircle, Clock, Zap, ChevronRight, Info
} from 'lucide-react';

// Wearable provider logos and info
const PROVIDERS = {
    apple_health: {
        name: 'Apple Health',
        icon: '🍎',
        platform: 'iOS',
        color: 'from-gray-800 to-gray-900',
        features: ['Herzfrequenz', 'HRV', 'Schlafphasen', 'Schritte', 'Workouts']
    },
    google_fit: {
        name: 'Google Fit',
        icon: '💚',
        platform: 'Android',
        color: 'from-green-500 to-teal-500',
        features: ['Herzfrequenz', 'Schlaf', 'Schritte', 'Aktivitäten']
    },
    fitbit: {
        name: 'Fitbit',
        icon: '⌚',
        platform: 'iOS & Android',
        color: 'from-cyan-500 to-blue-500',
        features: ['Herzfrequenz', 'HRV', 'Schlafphasen', 'Schritte', 'SpO2']
    },
    garmin: {
        name: 'Garmin',
        icon: '🏃',
        platform: 'iOS & Android',
        color: 'from-blue-600 to-indigo-600',
        features: ['Herzfrequenz', 'HRV', 'Body Battery', 'Schlaf', 'Training']
    },
    oura: {
        name: 'Oura Ring',
        icon: '💍',
        platform: 'iOS & Android',
        color: 'from-purple-500 to-pink-500',
        features: ['HRV', 'Schlafphasen', 'Readiness', 'Temperatur', 'SpO2']
    },
    whoop: {
        name: 'WHOOP',
        icon: '💪',
        platform: 'iOS & Android',
        color: 'from-red-500 to-orange-500',
        features: ['HRV', 'Strain', 'Recovery', 'Schlaf', 'Herzfrequenz']
    }
};

const content = {
    de: {
        title: 'Wearable Integration',
        subtitle: 'Verbinde deine Geräte für präzisere Daten',
        comingSoon: 'Native App in Entwicklung',
        comingSoonDesc: 'Die Wearable-Integration erfordert eine native iOS/Android App. Diese wird derzeit entwickelt.',
        nativeAppBenefits: 'Mit der nativen App kann Nanouk:',
        benefits: [
            'Automatisch deine Schlafdaten synchronisieren',
            'HRV-Daten für präzisen Readiness-Score nutzen',
            'Workout-Tracking in Echtzeit',
            'Push-Benachrichtigungen senden'
        ],
        supportedDevices: 'Unterstützte Geräte',
        dataTypes: 'Datentypen',
        heartRate: 'Herzfrequenz',
        hrv: 'HRV',
        sleep: 'Schlaf',
        steps: 'Schritte',
        activities: 'Aktivitäten',
        notifyMe: 'Benachrichtige mich',
        notifyMeDesc: 'Erhalte eine E-Mail, wenn die native App verfügbar ist',
        planned: 'Geplant',
        status: {
            connected: 'Verbunden',
            notConnected: 'Nicht verbunden',
            pending: 'Ausstehend'
        }
    },
    en: {
        title: 'Wearable Integration',
        subtitle: 'Connect your devices for more accurate data',
        comingSoon: 'Native App in Development',
        comingSoonDesc: 'Wearable integration requires a native iOS/Android app. This is currently being developed.',
        nativeAppBenefits: 'With the native app, Nanouk can:',
        benefits: [
            'Automatically sync your sleep data',
            'Use HRV data for precise Readiness Score',
            'Real-time workout tracking',
            'Send push notifications'
        ],
        supportedDevices: 'Supported Devices',
        dataTypes: 'Data Types',
        heartRate: 'Heart Rate',
        hrv: 'HRV',
        sleep: 'Sleep',
        steps: 'Steps',
        activities: 'Activities',
        notifyMe: 'Notify Me',
        notifyMeDesc: 'Get an email when the native app is available',
        planned: 'Planned',
        status: {
            connected: 'Connected',
            notConnected: 'Not Connected',
            pending: 'Pending'
        }
    }
};

// Nanouk with smartwatch
const NANOUK_WEARABLE = 'https://images.unsplash.com/photo-1563889958768-0fa816b019c7?w=200https://images.unsplash.com/photo-1767381604151-bae00f2fb337?w=200&h=200&fit=crop';

export default function WearableIntegration() {
    const { language } = useLanguage();
    const t = content[language] || content.de;
    const [notifyRequested, setNotifyRequested] = useState(false);

    const handleNotifyMe = () => {
        // In production, this would save the user's email preference
        setNotifyRequested(true);
        setTimeout(() => setNotifyRequested(false), 3000);
    };

    const dataTypes = [
        { key: 'heartRate', icon: Heart, color: 'text-red-500' },
        { key: 'hrv', icon: Activity, color: 'text-purple-500' },
        { key: 'sleep', icon: Moon, color: 'text-indigo-500' },
        { key: 'steps', icon: Footprints, color: 'text-green-500' },
        { key: 'activities', icon: Zap, color: 'text-orange-500' },
    ];

    return (
        <div className="p-4 lg:p-8 pb-24 lg:pb-8" data-testid="wearable-page">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="max-w-4xl mx-auto space-y-6"
            >
                {/* Header */}
                <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 rounded-2xl overflow-hidden shadow-lg">
                        <img src={NANOUK_WEARABLE} alt="Nanouk" className="w-full h-full object-cover" />
                    </div>
                    <div>
                        <h1 className="font-display text-2xl lg:text-3xl font-bold flex items-center gap-3">
                            <Watch className="w-8 h-8 text-cyan-500" />
                            {t.title}
                        </h1>
                        <p className="text-muted-foreground">{t.subtitle}</p>
                    </div>
                </div>

                {/* Coming Soon Banner */}
                <Card className="bg-gradient-to-r from-cyan-500/10 to-blue-500/10 border-cyan-500/30">
                    <CardContent className="p-6">
                        <div className="flex items-start gap-4">
                            <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
                                <Smartphone className="w-6 h-6 text-cyan-500" />
                            </div>
                            <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                    <h2 className="font-semibold text-lg">{t.comingSoon}</h2>
                                    <Badge variant="outline" className="text-cyan-500 border-cyan-500">
                                        {t.planned}
                                    </Badge>
                                </div>
                                <p className="text-muted-foreground text-sm mb-4">{t.comingSoonDesc}</p>
                                
                                <div className="mb-4">
                                    <p className="font-medium text-sm mb-2">{t.nativeAppBenefits}</p>
                                    <ul className="space-y-1">
                                        {t.benefits.map((benefit) => (
                                            <li key={`benefit-${benefit.slice(0, 24)}`} className="flex items-center gap-2 text-sm text-muted-foreground">
                                                <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                {benefit}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                <Button 
                                    onClick={handleNotifyMe}
                                    disabled={notifyRequested}
                                    className="gap-2"
                                >
                                    {notifyRequested ? (
                                        <>
                                            <CheckCircle2 className="w-4 h-4" />
                                            {language === 'de' ? 'Gespeichert!' : 'Saved!'}
                                        </>
                                    ) : (
                                        <>
                                            <Clock className="w-4 h-4" />
                                            {t.notifyMe}
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Data Types */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">{t.dataTypes}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
                            {dataTypes.map((type) => (
                                <div 
                                    key={type.key}
                                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-secondary/50"
                                >
                                    <type.icon className={`w-8 h-8 ${type.color}`} />
                                    <span className="text-sm font-medium">{t[type.key]}</span>
                                </div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* Supported Devices */}
                <Card>
                    <CardHeader>
                        <CardTitle className="text-lg">{t.supportedDevices}</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid gap-4">
                            {Object.entries(PROVIDERS).map(([key, provider]) => (
                                <motion.div
                                    key={key}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    className="flex items-center gap-4 p-4 rounded-xl border bg-card hover:bg-secondary/30 transition-colors"
                                >
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${provider.color} flex items-center justify-center text-2xl`}>
                                        {provider.icon}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-semibold">{provider.name}</span>
                                            <Badge variant="secondary" className="text-xs">
                                                {provider.platform}
                                            </Badge>
                                        </div>
                                        <div className="flex flex-wrap gap-1 mt-1">
                                            {provider.features.slice(0, 4).map((feature) => (
                                                <span key={`${provider.name}-${feature}`} className="text-xs text-muted-foreground bg-secondary px-2 py-0.5 rounded">
                                                    {feature}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                    <Badge variant="outline" className="text-muted-foreground">
                                        {t.status.pending}
                                    </Badge>
                                </motion.div>
                            ))}
                        </div>
                    </CardContent>
                </Card>

                {/* API Documentation Note */}
                <Card className="bg-secondary/30">
                    <CardContent className="p-4 flex items-start gap-3">
                        <Info className="w-5 h-5 text-muted-foreground flex-shrink-0 mt-0.5" />
                        <div className="text-sm text-muted-foreground">
                            <p className="font-medium mb-1">
                                {language === 'de' ? 'Für Entwickler:' : 'For Developers:'}
                            </p>
                            <p>
                                {language === 'de' 
                                    ? 'Die Backend-API für Wearable-Integration ist bereits fertig. Endpoints: /api/wearables/sync, /api/wearables/connect, /api/wearables/hrv/latest'
                                    : 'Backend API for wearable integration is ready. Endpoints: /api/wearables/sync, /api/wearables/connect, /api/wearables/hrv/latest'
                                }
                            </p>
                        </div>
                    </CardContent>
                </Card>
            </motion.div>
        </div>
    );
}
